import { todayIST } from "@templeos/shared";
import { Button, useToast } from "@templeos/ui";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLive, useNow } from "../components/hooks.js";
import { SupervisorModal } from "../components/SupervisorModal.js";
import { api, ApiError, isNetworkError, NetworkError } from "../lib/api.js";
import { ENV } from "../lib/config.js";
import { db } from "../lib/db.js";
import { admitGate, currentWhitelist, gateVerdict, isOfflineSlip, normalizeScan, offlineVerdict, perMinute, pulseGateRelay, recordRemoteAdmission, RESULT_TEXT, uploadScans, usedGateCodes, type GateResult, type GateWhitelist, type ScanReply, type ScanUploaders } from "../lib/gate.js";
import { useSession } from "../session.js";

interface Verdict { result: GateResult | "override" | "simulated"; title: string; detail: string; offline: boolean }

const OVERRIDE_REASONS = ["Relay outcome checked; entry not completed", "Damaged ticket, verified by hand", "VIP / protocol entry", "Scanner failure", "Elderly or medical assistance"] as const;
const WHITELIST_REFRESH_MS = 5 * 60_000;
const VERDICT_MS = 2_500;
const SCAN_TIMEOUT_MS = 3_000;  // a slow server still answers; only a dead link falls back to the offline list
const DOUBLE_READ_MS = 3_000;   // a scanner reading the same code twice must not turn a valid entry red

// ---- sounds (WebAudio, no files): a pleasant two-note chime and a loud buzzer
let audio: AudioContext | null = null;
function play(freqs: number[], type: OscillatorType, step: number, volume: number) {
  audio ??= new AudioContext();
  const ctx = audio;
  void ctx.resume();
  const t0 = ctx.currentTime;
  freqs.forEach((f, i) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type;
    osc.frequency.value = f;
    gain.gain.setValueAtTime(volume, t0 + i * step);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + (i + 1) * step);
    osc.connect(gain).connect(ctx.destination);
    osc.start(t0 + i * step);
    osc.stop(t0 + (i + 1) * step);
  });
}
const chime = () => play([880, 1318.5], "sine", 0.2, 0.35);
const buzzer = () => play([170, 150, 170], "square", 0.25, 0.6);

/** SOW Module 3: full-screen gate validation with anti-passback, offline whitelist, manual override and throughput. */
export function Gate() {
  const s = useSession();
  const toast = useToast();
  const gateCode = s.me?.counter?.code ?? "";
  const now = useNow(5_000);
  const [input, setInput] = useState("");
  const [verdict, setVerdict] = useState<Verdict | null>(null);
  const [entries, setEntries] = useState<number[]>([]);
  const [rejected, setRejected] = useState(0);
  const [live, setLive] = useState<Record<string, number>>({});
  const [wsUp, setWsUp] = useState(false);
  const [wl, setWl] = useState<{ day: string; count: number; at: string } | null>(null);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState<string | null>(null);
  const [overrideCode, setOverrideCode] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const whitelist = useRef<Set<string>>(new Set());
  const whitelistDay = useRef<string | null>(null);
  const whitelistRules = useRef<GateWhitelist | undefined>(undefined);
  const usedHere = useRef<Set<string>>(new Set());
  const usedDay = useRef<string | null>(null);
  const scanning = useRef(false);
  const storageFailed = useRef(false);
  const remoteWrites = useRef<Promise<void>>(Promise.resolve());
  const clearTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastRead = useRef<{ code: string; at: number } | null>(null);
  const waiting = useLive(() => db.scans.where("synced").equals(0).count(), "gate-scans", 0);

  useEffect(() => { if (!busy && !overrideOpen) inputRef.current?.focus(); }, [busy, overrideOpen]);

  const show = useCallback((v: Verdict) => {
    setVerdict(v);
    if (v.result === "valid" || v.result === "override") { chime(); setEntries((xs) => [...xs.filter((t) => Date.now() - t < 60 * 60_000), Date.now()]); }
    else if (v.result !== "simulated") { buzzer(); setRejected((n) => n + 1); }
    if (clearTimer.current) clearTimeout(clearTimer.current);
    clearTimer.current = v.result === "check" ? null : setTimeout(() => setVerdict(null), VERDICT_MS);
    inputRef.current?.focus();
  }, []);

  // Offline whitelist of today's valid codes, refreshed every 5 minutes, and the codes already let in today. The
  // day is read on every refresh: a gate left running past midnight moves to the new day's list.
  useEffect(() => {
    let day = "";
    let stopped = false;
    const load = async () => {
      const today = todayIST();
      try {
        if (today !== day) {
          day = today;
          whitelist.current = new Set();
          whitelistDay.current = null;
          whitelistRules.current = undefined;
          setWl(null);
          await db.meta.where("key").startsWith("whitelist:").filter((m) => m.key !== `whitelist:${today}`).delete();
        }
        try {
          const r = await api.get<Omit<GateWhitelist, "at">>(`/api/counters/gate/whitelist?day=${today}`, 20_000);
          const at = new Date().toISOString();
          const list = { ...r, at };
          if (stopped || !currentWhitelist(list, today)) return;
          await db.setMeta(`whitelist:${today}`, list);
          if (stopped || !currentWhitelist(list, today)) return;
          whitelist.current = new Set(r.codes);
          whitelistDay.current = r.day;
          whitelistRules.current = list;
          setWl({ day: r.day, count: r.codes.length, at });
        } catch {
          const cached = await db.getMeta<GateWhitelist>(`whitelist:${today}`);
          if (!stopped && currentWhitelist(cached, today)) {
            whitelist.current = new Set(cached.codes); whitelistDay.current = cached.day;
            whitelistRules.current = cached;
            setWl({ day: cached.day, count: cached.codes.length, at: cached.at });
          }
        }
      } catch {
        storageFailed.current = true;
        if (!stopped) show({ result: "check", title: "Gate storage unavailable", detail: "Entry is blocked. Restore local storage before scanning.", offline: false });
      }
    };
    void load();
    const iv = setInterval(load, WHITELIST_REFRESH_MS);
    return () => { stopped = true; clearInterval(iv); };
  }, [show]);

  // Upload offline entries so the server marks those tickets used at every gate, and offline refusals for the audit trail.
  useEffect(() => {
    const post: ScanUploaders = {
      entries: (scans) => api.post("/api/counters/gate/sync", { scans }, 30_000),
      refused: (scans) => api.post("/api/counters/gate/refused", { scans }, 30_000),
    };
    const push = async () => {
      try {
        await uploadScans(db, post);
      } catch (e) {
        if (!isNetworkError(e)) toast(`Offline gate entries could not be uploaded: ${(e as Error).message}`, "danger");
      }
    };
    void push();
    const iv = setInterval(push, 30_000);
    window.addEventListener("online", push);
    return () => { clearInterval(iv); window.removeEventListener("online", push); };
  }, [toast]);

  // Live entries at every gate (WebSocket channel "gates").
  useEffect(() => {
    let ws: WebSocket | null = null;
    let retry: ReturnType<typeof setTimeout> | null = null;
    let stopped = false;
    const connect = () => {
      ws = new WebSocket(`${location.protocol === "https:" ? "wss" : "ws"}://${location.host}/api/ws?channels=gates`);
      ws.onopen = () => setWsUp(true);
      ws.onclose = () => { setWsUp(false); if (!stopped) retry = setTimeout(connect, 5_000); };
      ws.onmessage = (m) => {
        try {
          const msg = JSON.parse(String(m.data)) as { channel: string; payload?: { gateCode?: string; result?: string; code?: string | null; at?: string } };
          const g = msg.payload?.gateCode;
          if (msg.channel !== "gates" || !g || msg.payload?.result !== "valid") return;
          setLive((x) => ({ ...x, [g]: (x[g] ?? 0) + 1 }));
          if (msg.payload.code) {
            const code = msg.payload.code;
            const at = msg.payload.at ?? "";
            // Queue persistence immediately; scans wait for it before making any offline decision.
            remoteWrites.current = remoteWrites.current.then(async () => {
              await recordRemoteAdmission(db, code, g, at);
              if (todayIST(new Date(at)) === todayIST()) usedHere.current.add(code);
            }).catch(() => {
              storageFailed.current = true;
              show({ result: "check", title: "Gate storage unavailable", detail: "A remote entry could not be saved. Entry is blocked; restore storage and reconnect.", offline: false });
            });
          }
        } catch {
          /* heartbeat or unrelated frame */
        }
      };
    };
    connect();
    return () => { stopped = true; if (retry) clearTimeout(retry); ws?.close(); };
  }, [show]);

  const scan = async (raw: string) => {
    if (scanning.current) return;
    scanning.current = true;
    setBusy(true);
    try {
      setInput("");
      const code = normalizeScan(raw);
      if (!code) {
        setRecoveryCode(null); setOverrideCode(null);
        return show({ result: "invalid", title: RESULT_TEXT.invalid, detail: "This is not a temple ticket code.", offline: false });
      }
      const now = Date.now();
      if (lastRead.current?.code === code && now - lastRead.current.at < DOUBLE_READ_MS) return; // the scanner read it twice
      lastRead.current = { code, at: now };
      setRecoveryCode(null); setOverrideCode(code);
      await remoteWrites.current;
      if (storageFailed.current) return show({ result: "check", title: "Gate storage unavailable", detail: "Entry is blocked. Restore local storage and reconnect before scanning.", offline: false });
      const today = todayIST();
      if (usedDay.current !== today) { usedHere.current = await usedGateCodes(db, today); usedDay.current = today; }
      let decided: { reply: ScanReply; offline: boolean };
      let offlineDay: string | null = null;
      try {
        // A dead link or a failing server falls back to the offline list.
        decided = await gateVerdict(code, (c) => api.post<ScanReply>("/api/counters/gate/scan", { code: c, gateCode }, SCAN_TIMEOUT_MS),
          (e) => e instanceof NetworkError || (e instanceof ApiError && e.status >= 500),
          () => {
            offlineDay = whitelistDay.current;
            return storageFailed.current ? "check" : offlineVerdict(code, whitelist.current, usedHere.current, offlineDay, todayIST(), whitelistRules.current);
          });
      } catch (e) {
        if (e instanceof ApiError) return show({ result: "invalid", title: "Scan refused", detail: e.message, offline: false }); // a refusal is final
        throw e;
      }
      const { reply: r, offline } = decided;
      const t = r.ticket;
      const entered = t?.code ?? code; // an offline slip is let in as the ticket it stands for
      setOverrideCode(entered);
      if (r.result === "valid") {
        await remoteWrites.current;
        if (storageFailed.current) throw new Error("Local admission history is unavailable.");
        usedHere.current.add(entered);
        const result = await admitGate(db, { code: entered, gateCode, scannedAt: new Date().toISOString(), synced: offline ? 0 : 1 }, () => pulseGateRelay(ENV.agentUrl, gateCode), offlineDay,
          () => offlineVerdict(entered, whitelist.current, new Set(), offlineDay, todayIST(), whitelistRules.current) === "valid");
        if (result === "stale") return show({ result: "check", title: "Admission window changed", detail: "Entry is blocked. Refresh today's whitelist and have a supervisor check this ticket's date and slot.", offline });
        if (result === "relay_failed") {
          setRecoveryCode(entered);
          return show({ result: "check", title: "Gate opening not confirmed", detail: "Ticket remains used. Check whether entry occurred; a supervisor must authorize any recovery.", offline });
        }
        if (result === "used") return show({ result: "used", title: RESULT_TEXT.used, detail: "Entry was already recorded on this terminal.", offline });
        setRecoveryCode(null);
        setOverrideCode(null);
        if (result === "simulated") return show({ result: "simulated", title: "Simulated gate entry", detail: "Device agent is in simulator mode. No physical gate was opened; the ticket remains consumed for this test.", offline });
      } else if (r.result === "used") {
        const lastRelay = await db.scans.where("code").equals(entered).reverse().filter(x => x.relay !== undefined).first();
        if (lastRelay?.relay === "pending" || lastRelay?.relay === "uncertain") {
          setRecoveryCode(entered);
          return show({ result: "check", title: "Supervisor recovery required", detail: "Ticket remains used after an unconfirmed opening. Check whether entry occurred before authorizing another pulse.", offline });
        }
      }
      const list = wl ? `offline list of ${wl.count} codes from ${new Date(wl.at).toLocaleTimeString("en-IN", { timeStyle: "short" })}` : "no offline list on this gate";
      const detail = offline
        ? r.result === "wrong_date" ? "This pass is outside its darshan slot and grace period. Entry is blocked."
          : r.result !== "check" ? `Checked against the ${list}`
          : isOfflineSlip(code) ? `Slip printed while the counter was offline (${code}). Check the receipt; use Manual override to admit.` : `Not on the ${list}. Check the receipt by hand.`
        : r.result === "used" && t?.usedAt ? `Used at ${t.usedGate ?? "a gate"} ${new Date(t.usedAt).toLocaleTimeString("en-IN", { timeStyle: "short" })}`
          : r.message ?? (t ? `${t.itemName}${t.slot ? ` · ${t.slot}` : ""}` : code);
      show({ result: r.result, title: RESULT_TEXT[r.result] ?? r.result, detail, offline });
      // Kept locally too, so a restart into offline mode still refuses a second entry today; offline entries upload later.
      // A scan refused offline goes to the server's audit trail only, never to the entry upload that spends tickets.
      if (r.result !== "valid" && offline) await db.scans.add({ code, gateCode, scannedAt: new Date().toISOString(), verdict: r.result, synced: 0 });
    } catch {
      storageFailed.current = true;
      show({ result: "check", title: "Entry could not be completed", detail: "Do not retry the gate. Restore local storage; a supervisor must check any consumed ticket before recovery.", offline: false });
    } finally { scanning.current = false; setBusy(false); }
  };

  const cls = !verdict ? "" : verdict.result === "valid" ? "valid" : verdict.result === "override" || verdict.result === "simulated" || verdict.result === "check" ? "override" : "bad";
  const perMin = perMinute(entries, now);
  return (
    <div className="pos-gate">
      <header className="pos-gate-top">
        <strong style={{ fontSize: 20 }}>{s.me?.counter ? `${s.me.counter.code} · ${s.me.counter.name}` : "Gate"}</strong>
        <span className={`pos-pill${s.reachable ? "" : " bad"}`}>{s.reachable ? "Online" : "Offline mode"}</span>
        <span className={`pos-pill${wsUp ? "" : " warn"}`}>{wsUp ? "Live link" : "Live link down"}</span>
        <span className="pos-pill">{wl?.day === todayIST() ? `Whitelist ${wl.count} codes · ${new Date(wl.at).toLocaleTimeString("en-IN", { timeStyle: "short" })}` : "No current whitelist"}</span>
        {waiting > 0 && <span className="pos-pill warn">{waiting} offline scans to upload</span>}
        <span className="tos-spacer" />
        <Button disabled={busy} onClick={() => setOverrideOpen(true)}>{recoveryCode ? "Supervisor recovery" : "Manual override"}</Button>
        <Button onClick={s.lock}>Lock</Button>
      </header>
      <main className={`pos-gate-screen ${cls}`} onClick={() => inputRef.current?.focus()}>
        <div className="pos-gate-verdict" role="status" aria-live="assertive">{verdict ? verdict.title : "Ready - scan ticket"}</div>
        <div className="pos-gate-detail">{verdict ? `${verdict.detail}${verdict.offline ? " (offline)" : ""}` : "Hold the QR code or barcode under the scanner"}</div>
        <form onSubmit={(e) => { e.preventDefault(); if (input.trim()) void scan(input); }}>
          <label htmlFor="gate-scan" className="tos-sr-only">Ticket code</label>
          <input id="gate-scan" ref={inputRef} className="pos-gate-input" disabled={busy || overrideOpen} autoFocus autoComplete="off" value={input} placeholder="Scan or type ticket code"
            onChange={(e) => setInput(e.target.value)}
            onBlur={() => setTimeout(() => {
              // Back to the scanner, unless the operator moved to a control (keyboard users reach Override and Lock).
              const at = document.activeElement;
              if ((!at || at === document.body) && !document.querySelector(".tos-overlay, .pos-lock")) inputRef.current?.focus();
            }, 50)} />
        </form>
      </main>
      <footer className="pos-gate-stats">
        <div className="pos-gate-stat"><b>{entries.length}</b><span>Entries this hour</span></div>
        <div className="pos-gate-stat"><b>{perMin}</b><span>Per minute (5 min)</span></div>
        <div className="pos-gate-stat"><b>{rejected}</b><span>Refused</span></div>
        <div className="pos-gate-stat"><b>{Object.values(live).reduce((a, b) => a + b, 0)}</b>
          <span>Tickets validated (live){Object.keys(live).length ? `: ${Object.entries(live).map(([g, n]) => `${g} ${n}`).join(" · ")}` : ""}</span></div>
      </footer>
      <SupervisorModal open={overrideOpen} title="Manual gate override" reasons={OVERRIDE_REASONS} confirmLabel="Approve and open gate" onClose={() => { setOverrideOpen(false); inputRef.current?.focus(); }}
        onApprove={async ({ username, password, reason }) => {
          if (scanning.current || storageFailed.current) throw new Error("Gate is busy or storage is unavailable. Restore it before recovery.");
          scanning.current = true; setBusy(true);
          try {
            await remoteWrites.current;
            if (storageFailed.current) throw new Error("Local admission history is unavailable. Restore storage and reload before recovery.");
            const ticketCode = recoveryCode ?? overrideCode;
            await api.post("/api/counters/gate/override", { gateCode, reason, ...(ticketCode ? { code: ticketCode } : {}), supervisor: { username, password } });
            const code = ticketCode ?? `MANUAL:${crypto.randomUUID()}`;
            let opened;
            try {
              opened = await admitGate(db, { code, gateCode, scannedAt: new Date().toISOString(), synced: 1 }, () => pulseGateRelay(ENV.agentUrl, gateCode));
            } catch {
              storageFailed.current = true;
              show({ result: "check", title: "Manual entry not confirmed", detail: "Local admission history is unavailable. Check physical entry and restore storage before recovery.", offline: false });
              throw new Error("Local admission history is unavailable. Check physical entry and restore storage before another attempt.");
            }
            if (opened !== "opened" && opened !== "simulated") {
              setRecoveryCode(code);
              show({ result: "check", title: "Gate opening not confirmed", detail: "Supervisor approval was recorded. Check the physical gate before authorizing another attempt.", offline: false });
              throw new Error("Relay did not confirm opening. Check whether entry occurred; another attempt requires supervisor approval.");
            }
            setRecoveryCode(null);
            setOverrideCode(null);
            if (opened === "simulated") return show({ result: "simulated", title: "Simulated supervisor override", detail: "Approval was recorded, but the device agent only simulated the pulse. No physical gate was opened.", offline: false });
            show({ result: "override", title: "Override - entry allowed", detail: `Approved by ${username}: ${reason}`, offline: false });
          } finally { scanning.current = false; setBusy(false); }
        }}>
        <p style={{ margin: 0 }}>{recoveryCode
          ? `Recovery for ${recoveryCode}. First verify that entry did not occur. A supervisor must authorize a new pulse; the ticket remains used.`
          : overrideCode ? `Override for ${overrideCode}. The supervisor must check this pass and approve the exception. The ticket will be marked used when it is still valid.`
            : "Opens the turnstile once without a valid ticket. The supervisor, reason and time are recorded in the audit trail."}</p>
      </SupervisorModal>
    </div>
  );
}
